/* ==========================================================================
   emporo — one-tap checkout. Two sheets, one controller.

   THE FLOW
     product  Buy now   -> email -> POST /checkout/create {sku}   -> Razorpay
     cart     Check out -> email -> POST /checkout/create {skus}  -> Razorpay
                        -> POST /checkout/verify -> download link(s), same sheet

   THE BROWSER NEVER SENDS A PRICE. It posts SKUs and an email; the server
   reads the amounts from its own packs table. Anything this file could post,
   a buyer could edit in devtools first.

   THE BROWSER IS NOT THE AUTHORITY ON SUCCESS EITHER. Razorpay's webhook and
   the sweep settle the order server-side, so a customer who loses the tab
   mid-payment still gets the link by email. Everything here is the fast path
   — which is why a failure AFTER payment is never shown as a payment error.

   CART MODE (DESIGN_V2.md v3 rule 8). While the API setting is set the
   snippet emporo-cart-sheet.liquid renders one global <dialog>, and a
   capture-phase listener on `document` takes every Shopify checkout entry
   (button[name=checkout] in the drawer and on the cart page, /checkout
   links, checkout forms): it stops the navigation, reads /cart.js, and opens
   the sheet with the cart's SKUs. With the setting blank the snippet is not
   rendered, nothing is intercepted, Shopify's checkout is untouched. The
   listener is delegated, so the drawer re-rendering underneath it (morph,
   section:load) changes nothing.

   A modal <dialog> makes everything outside it inert, Razorpay's iframe
   included, so the sheet — and any open Horizon drawer, which is a modal
   dialog too — is closed for the hand-off and the sheet re-opened when
   Razorpay returns. The body lock and the flow state survive that gap.

   Razorpay's checkout.js (~100KB) is loaded on demand, never on page load.

   Analytics hook (no third-party code):
     document.addEventListener('emporo:checkout', e => e.detail.step)
     steps: open | create | paid | done | error | dismiss
     detail carries `sku` (product sheet) or `mode:'cart', skus[], count`
     (cart sheet); create/paid also carry the Razorpay order id.
   ========================================================================== */

(function () {
  'use strict';

  var RZP_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
  var RZP_THEME = '#0b0b0c';
  var CREATE_TIMEOUT_MS = 20000;
  var VERIFY_TIMEOUT_MS = 30000;
  var CART_TIMEOUT_MS = 15000;
  var EMAIL_KEY = 'emporo:checkout:email';
  var LOCK_CLASS = 'e-buy-lock';
  var CART_EVENT = 'shopify:cart:lines-update';   // what Horizon's drawer, icon and count listen for
  var CHECKOUT_SEL = 'button[name="checkout"], a[href*="/checkout"], form[action*="/checkout"] [type="submit"]';

  var COPY = {
    opening: 'Opening Razorpay…',
    confirming: 'Confirming payment…',
    free: 'Get it free',
    doneFree: 'Yours. Your pack is ready.',
    doneFreePacks: 'Yours. Your packs are ready.',
    codeInvalid: 'That code does not apply here.',
    codeUnavailable: 'Could not check the code just now. Try again.',
    needEmail: 'Enter the email your link should go to.',
    badEmail: 'That email does not look right.',
    couldNotSend: 'Could not save that email just now. Your link above still works.',
    unavailable: 'This pack is not available to buy right now.',
    provider: 'Could not reach the payment provider. Check your connection.',
    couldNotStart: 'Could not start the payment. Nothing was charged — try again.',
    failed: 'That payment did not go through. Nothing was charged — try again.',
    dismissed: 'Payment cancelled. Nothing was charged.',
    doneTitle: 'Paid. Your pack is ready.',
    donePacks: 'Paid. Your packs are ready.',
    lateTitle: 'Paid. Confirming your link.',
    lateBody: 'The payment went through. Our confirmation call did not — it is retried automatically on our side. If the link has not appeared within a few minutes, email brandrop.in@gmail.com with the payment id below and it is sent by hand.',
    // cart mode
    cartUnreadable: 'Could not read your cart. Close this and try again.',
    nothingToBuy: 'Nothing in the cart can be bought here yet.',
    someNotForSale: 'One of these packs is not available to buy right now. Remove it from the cart and try again.',
    removed: 'Removed. The rest of your cart is above.',
    removeFailed: 'Could not remove it. Open the cart and remove it there.',
    clearing: 'Clearing…',
    cleared: 'Cart cleared',
    clearFailed: 'Could not clear the cart — open it and remove the packs by hand.'
  };

  /* ---- helpers ---------------------------------------------------------- */

  function closest(node, selector) {
    return node && node.closest ? node.closest(selector) : null;
  }

  function merge(base, extra) {
    var out = {};
    Object.keys(base || {}).forEach(function (k) { out[k] = base[k]; });
    Object.keys(extra || {}).forEach(function (k) { out[k] = extra[k]; });
    return out;
  }

  function emit(step, detail) {
    var d = merge({ step: step }, detail);
    try {
      document.dispatchEvent(new CustomEvent('emporo:checkout', { detail: d }));
    } catch (e) { /* no CustomEvent constructor: nothing to report to */ }
  }

  var rzpLoading = null;
  function loadRazorpay() {
    if (window.Razorpay) return Promise.resolve(true);
    if (rzpLoading) return rzpLoading;
    rzpLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = RZP_SRC;
      s.async = true;
      s.onload = function () { resolve(true); };
      s.onerror = function () { rzpLoading = null; reject(new Error('rzp_load_failed')); };
      document.head.appendChild(s);
    });
    return rzpLoading;
  }

  // fetch with a hard timeout. Resolves {status, data}; rejects with
  // Error('timeout') when the clock runs out (and aborts the request).
  function request(method, url, body, timeoutMs) {
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = null;
    var opts = {
      method: method,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: ctrl ? ctrl.signal : undefined
    };
    if (body !== undefined) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    var req = fetch(url, opts).then(function (r) {
      return r.json().catch(function () { return {}; })
        .then(function (data) { return { status: r.status, data: data || {} }; });
    });
    var clock = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (ctrl) { try { ctrl.abort(); } catch (e) { /* already settled */ } }
        reject(new Error('timeout'));
      }, timeoutMs);
    });
    return Promise.race([req, clock]).then(
      function (res) { clearTimeout(timer); return res; },
      function (err) { clearTimeout(timer); throw err; }
    );
  }
  function postJSON(url, body, timeoutMs) { return request('POST', url, body, timeoutMs); }
  function getJSON(url, timeoutMs) { return request('GET', url, undefined, timeoutMs); }

  // Same rule as the rail (checkout.create_checkout), a little stricter on
  // whitespace and a second "@". The server is still the authority.
  function validEmail(value) {
    var at = value.indexOf('@');
    if (at < 1) return false;
    if (value.indexOf('@', at + 1) >= 0) return false;
    if (/\s/.test(value)) return false;
    var domain = value.slice(at + 1);
    var dot = domain.indexOf('.');
    return dot > 0 && dot < domain.length - 1;
  }

  function remember(email) {
    try { window.sessionStorage.setItem(EMAIL_KEY, email); } catch (e) { /* private mode */ }
  }
  function recall() {
    try { return window.sessionStorage.getItem(EMAIL_KEY) || ''; } catch (e) { return ''; }
  }

  function describe(reason) {
    switch (reason) {
      case 'unknown_sku':
      case 'no_price_for_sku':
        return COPY.unavailable;
      case 'bad_email':
        return COPY.badEmail;
      case 'rzp_load_failed':
        return COPY.provider;
      default:
        return COPY.couldNotStart;
    }
  }

  function focusQuiet(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  function visible(el) {
    return !!(el && el.isConnected !== false && (el.offsetParent !== null || el === document.body));
  }

  // Shopify's money format ("Rs. {{amount}}", "₹{{amount_no_decimals}}", …)
  // applied to a minor-unit integer, grouping by threes like `| money` does.
  function formatMoney(minor, format, currency) {
    var n = Math.round(Number(minor) || 0);
    var neg = n < 0;
    if (neg) n = -n;
    var whole = String(Math.floor(n / 100));
    var frac = String(n % 100);
    if (frac.length < 2) frac = '0' + frac;
    function group(sep) { return whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep); }
    var out;
    if (format && format.indexOf('{{') >= 0) {
      out = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) {
        switch (key) {
          case 'amount_no_decimals': return group(',');
          case 'amount_with_comma_separator': return group('.') + ',' + frac;
          case 'amount_no_decimals_with_comma_separator': return group('.');
          case 'amount_with_apostrophe_separator': return group('\'') + '.' + frac;
          case 'amount_no_decimals_with_space_separator': return group(' ');
          case 'amount_with_space_separator': return group(' ') + ',' + frac;
          case 'amount_with_period_and_space_separator': return group(' ') + '.' + frac;
          default: return group(',') + '.' + frac;
        }
      });
    } else {
      out = group(',') + '.' + frac + (currency ? ' ' + currency : '');
    }
    return (neg ? '-' : '') + out.replace(/^\s+|\s+$/g, '');
  }

  // Any open Horizon drawer is a modal <dialog>: left open it would keep
  // Razorpay's iframe inert. Closed here, before the hand-off.
  function closeDrawers() {
    document.querySelectorAll('theme-drawer[open]').forEach(function (d) {
      try {
        if (typeof d.close === 'function') d.close();
        else {
          var dlg = d.querySelector('dialog[open]');
          if (dlg) dlg.close();
        }
      } catch (e) { /* a drawer mid-animation: nothing to do */ }
    });
  }

  /* ---- the sheet controller: everything both sheets share ---------------
     cfg: api           base URL, no trailing slash
          payload(addr) the create body — {sku, email} or {skus, email}
          description() the line Razorpay shows
          detail(extra) analytics detail base ({sku} or {mode, skus, count})
          onRefused(reason, data) -> true when the sheet has shown its own copy
          onFinish(grants, addr, emailed) / onLate() / onCreated(order)
     Returns the controller, or null when the markup is incomplete.
     ---------------------------------------------------------------------- */

  function Sheet(root, cfg) {
    var api = cfg.api;
    var sheet = root.querySelector('[data-e-sheet]');
    var handle = root.querySelector('[data-e-handle]');
    var closeBtn = root.querySelector('[data-e-close]');
    var form = root.querySelector('[data-e-step="email"]');
    var email = root.querySelector('[data-e-email]');
    var msg = root.querySelector('[data-e-msg]');
    var payBtn = root.querySelector('[data-e-pay]');
    var status = root.querySelector('[data-e-status]');
    var doneTitle = root.querySelector('[data-e-done-title]');
    var doneBody = root.querySelector('[data-e-done-body]');
    var doneNote = root.querySelector('[data-e-done-note]');
    var download = root.querySelector('[data-e-download]');   // product sheet: one link
    var links = root.querySelector('[data-e-links]');         // cart sheet: one per grant
    var linkTpl = root.querySelector('[data-e-link-template]');
    // Pay-first: the done step can take an email AFTER payment, shown only
    // when the rail got none from Razorpay's own form (optional markup).
    var doneForm = root.querySelector('[data-e-done-form]');
    var doneEmail = root.querySelector('[data-e-done-email]');
    var doneSend = root.querySelector('[data-e-done-send]');
    var doneMsg = root.querySelector('[data-e-done-msg]');
    var checkoutId = '';
    var steps = {
      email: form,
      paying: root.querySelector('[data-e-step="paying"]'),
      done: root.querySelector('[data-e-step="done"]')
    };
    if (!sheet || !form || !email || !payBtn || !(download || links)) return null;

    /* ---- discount code (optional markup; the sheet works without it) ----
       Shopify prices the code, the rail charges it. The sheet posts the code
       to /checkout/quote and paints whatever comes back; on create the same
       code goes with the SKUs and the server quotes again from scratch. */
    var codeToggle = root.querySelector('[data-e-code-toggle]');
    var codeWrap = root.querySelector('[data-e-code-wrap]');
    var codeInput = root.querySelector('[data-e-code]');
    var codeApply = root.querySelector('[data-e-code-apply]');
    var codeMsg = root.querySelector('[data-e-code-msg]');
    var payVerb = root.querySelector('[data-e-pay-verb]');
    var payAmountEl = root.querySelector('[data-e-pay-amount]');
    var applied = '';          // the code the last good quote honoured
    var quoting = false;

    var canModal = typeof sheet.showModal === 'function';

    function codeSay(text, tone) {
      if (!codeMsg) return;
      if (!text) { codeMsg.hidden = true; codeMsg.textContent = ''; codeMsg.classList.remove('is-error'); return; }
      codeMsg.textContent = text;
      codeMsg.classList.toggle('is-error', tone === 'error');
      codeMsg.hidden = false;
    }

    function fmt(minor) { return cfg.money ? cfg.money(minor) : ''; }

    // "Pay ₹299" → "Pay ₹150" → "Get it free". Never a price the page made up:
    // amount is what the rail's quote or create said.
    function paintPay(amount) {
      if (!payAmountEl || typeof amount !== 'number') return;
      if (amount <= 0) {
        if (payVerb) payVerb.textContent = COPY.free;
        payAmountEl.hidden = true;
      } else {
        if (payVerb) payVerb.textContent = 'Pay';
        payAmountEl.hidden = false;
        var text = fmt(amount);
        if (text) payAmountEl.textContent = text;
      }
    }

    function quoteBody(code) {
      var body = cfg.payload('');
      delete body.email;
      body.code = code;
      return body;
    }

    function applyCode(code, done) {
      code = String(code || '').replace(/^\s+|\s+$/g, '').toUpperCase();
      if (codeInput && codeInput.value !== code) codeInput.value = code;
      if (!code) {
        applied = '';
        codeSay('');
        if (cfg.onQuote) cfg.onQuote(null);
        paintPay(cfg.base ? cfg.base() : null);
        if (done) done(true);
        return;
      }
      if (quoting) return;
      quoting = true;
      if (codeApply) codeApply.disabled = true;
      function settle(ok, q, reason, httpOk) {
        quoting = false;
        if (codeApply) codeApply.disabled = false;
        if (ok) {
          applied = (q.discount && q.discount.code) || code;
          if (cfg.onQuote) cfg.onQuote(q);
          paintPay(q.amount_minor);
          var off = q.discount ? q.discount.amount_minor : 0;
          codeSay(applied + ' applied' + (off && fmt(off) ? ' · ' + fmt(off) + ' off' : ''), 'note');
          emit('code', cfg.detail({ code: applied, off: off, amount: q.amount_minor }));
        } else {
          applied = '';
          if (cfg.onQuote) cfg.onQuote(null);
          paintPay(cfg.base ? cfg.base() : null);
          codeSay(reason === 'discount_unavailable' ? COPY.codeUnavailable
            : (httpOk ? COPY.codeInvalid : describe(reason)), 'error');
        }
        if (done) done(ok);
      }
      postJSON(api + '/checkout/quote', quoteBody(code), CREATE_TIMEOUT_MS).then(function (res) {
        var q = res.data || {};
        var good = res.status === 200 && q.ok && q.discount && q.discount.applicable;
        settle(!!good, q, q.reason || q.error || '', res.status === 200);
      }).catch(function () {
        settle(false, null, 'discount_unavailable', false);
      });
    }

    if (codeToggle && codeWrap) {
      codeToggle.addEventListener('click', function () {
        var show = codeWrap.hidden;
        codeWrap.hidden = !show;
        codeToggle.setAttribute('aria-expanded', show ? 'true' : 'false');
        if (show) focusQuiet(codeInput);
      });
    }
    if (codeApply) {
      codeApply.addEventListener('click', function () { applyCode(codeInput ? codeInput.value : ''); });
    }
    if (codeInput) {
      codeInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') { event.preventDefault(); applyCode(codeInput.value); }
      });
      // Editing an applied code un-applies it: the price on screen must
      // always belong to the code in the box.
      codeInput.addEventListener('input', function () {
        if (applied && codeInput.value.replace(/^\s+|\s+$/g, '').toUpperCase() !== applied) {
          applied = '';
          codeSay('');
          if (cfg.onQuote) cfg.onQuote(null);
          paintPay(cfg.base ? cfg.base() : null);
        }
      });
    }

    // idle | email | opening | razorpay | confirming | done
    var state = 'idle';
    var busy = false;
    var locked = false;
    var payable = true;
    var lastFailure = '';
    var lastPaymentId = '';

    var ctrl = { returnTo: null };

    function inFlight() {
      return state === 'opening' || state === 'razorpay' || state === 'confirming';
    }

    function step(name) {
      Object.keys(steps).forEach(function (k) {
        if (steps[k]) steps[k].hidden = k !== name;
      });
      sheet.setAttribute('data-step', name);
      if (closeBtn) closeBtn.hidden = name === 'paying';
    }

    function say(text, tone) {
      if (!msg) return;
      if (!text) {
        msg.hidden = true;
        msg.textContent = '';
        msg.classList.remove('is-error');
        return;
      }
      msg.textContent = text;
      msg.classList.toggle('is-error', tone === 'error');
      msg.hidden = false;
    }

    function markInvalid(text) {
      say(text, 'error');
      email.setAttribute('aria-invalid', 'true');
    }
    function markValid() {
      email.removeAttribute('aria-invalid');
    }

    function syncPay() {
      payBtn.disabled = busy || !payable;
    }

    function lock() {
      if (locked) return;
      locked = true;
      document.documentElement.classList.add(LOCK_CLASS);
    }
    function release() {
      if (!locked) return;
      locked = false;
      document.documentElement.classList.remove(LOCK_CLASS);
    }

    function present() {
      lock();
      if (sheet.open) return;
      if (canModal) sheet.showModal();
      else sheet.setAttribute('open', '');
    }

    function resetIdle() {
      release();
      state = 'idle';
      busy = false;
      syncPay();
    }

    // The trigger may sit in a drawer that was closed for the hand-off; then
    // the header's cart control is the nearest sensible place.
    function returnFocus() {
      var target = ctrl.returnTo;
      if (!visible(target)) target = document.querySelector('[data-e-cart][aria-controls], a[href*="/cart"]');
      focusQuiet(target);
    }

    // Runs after any close, ours or the browser's (Escape, close watcher).
    // Idempotent: the browser queues its close event as an animation-frame
    // task, so it can land late or twice relative to our own synchronous
    // reset, and it must do nothing if the sheet was re-opened meanwhile.
    function onClosed() {
      if (sheet.open) return;
      // Closed during the Razorpay hand-off, or forced shut mid-flight by the
      // browser's close watcher: keep the lock and the state; the result will
      // re-present the sheet when it arrives.
      if (inFlight()) return;
      if (state === 'idle') return;
      resetIdle();
      returnFocus();
    }

    function hide() {
      if (sheet.open) {
        if (canModal) sheet.close();
        else sheet.removeAttribute('open');
      }
      onClosed();
    }

    // Our side may close the sheet only on the email and done steps.
    function dismiss() {
      if (inFlight()) return false;
      hide();
      return true;
    }

    /* ---- the sheet's own events ---- */

    sheet.addEventListener('cancel', function (event) {
      // Escape. Blocked while a payment is in flight.
      if (inFlight()) { event.preventDefault(); return; }
      // The browser closes it next; make sure the cleanup runs even where
      // no close event follows (Chrome 148 fires only `toggle`).
      window.setTimeout(onClosed, 0);
    });

    // `close` is the classic signal; `toggle` (newState "closed") is what
    // newer Chrome fires — some builds fire only the latter. onClosed is
    // idempotent, so hearing both costs nothing.
    sheet.addEventListener('close', onClosed);
    sheet.addEventListener('toggle', function (event) {
      if (!event || event.newState === 'closed') onClosed();
    });

    sheet.addEventListener('click', function (event) {
      // The dialog box has no padding of its own, so a click whose target is
      // the dialog element can only have landed on the backdrop.
      if (event.target === sheet) dismiss();
    });

    if (closeBtn) closeBtn.addEventListener('click', function () { dismiss(); });

    /* ---- drag handle: pull down to close (email/done steps only) ---- */

    if (handle && 'ontouchstart' in window) {
      var y0 = null;
      var dy = 0;
      var endDrag = function () {
        if (y0 === null) return;
        y0 = null;
        sheet.classList.remove('is-dragging');
        sheet.style.removeProperty('--e-buy-drag');
        if (dy > 90) dismiss();
        dy = 0;
      };
      handle.addEventListener('touchstart', function (event) {
        if (inFlight() || !event.touches.length) return;
        y0 = event.touches[0].clientY;
        dy = 0;
        sheet.classList.add('is-dragging');
      }, { passive: true });
      handle.addEventListener('touchmove', function (event) {
        if (y0 === null || !event.touches.length) return;
        dy = Math.max(0, event.touches[0].clientY - y0);
        sheet.style.setProperty('--e-buy-drag', dy + 'px');
      }, { passive: true });
      handle.addEventListener('touchend', endDrag);
      handle.addEventListener('touchcancel', endDrag);
    }

    /* ---- open ---- */

    // Returns false while a payment is in flight (nothing to re-open onto).
    // `prep` runs after the reset and before the sheet shows — the cart
    // sheet fills its list there, so the open event carries the SKUs.
    function open(prep) {
      if (busy) return false;
      state = 'email';
      lastFailure = '';
      if (!email.value) email.value = recall();
      markValid();
      say('');
      if (prep) prep();
      // PAY-FIRST (default for the product sheet): the tap goes straight to
      // Razorpay, whose own form collects contact and email. The email step
      // is still here — it is where a dismissed or failed payment lands, and
      // where a discount code is typed — but nobody has to fill it to buy.
      // The cart sheet keeps the form first (its items list lives there).
      if (cfg.instant !== false && !(codeInput && codeInput.value.replace(/^\s+|\s+$/g, ''))) {
        step('paying');
        present();
        emit('open', cfg.detail());
        start(email.value.trim());
        return true;
      }
      step('email');
      present();
      emit('open', cfg.detail());
      window.setTimeout(function () { focusQuiet(email); }, 30);
      return true;
    }

    /* ---- email step ---- */

    email.addEventListener('blur', function () {
      var value = email.value.trim();
      if (value && !validEmail(value)) markInvalid(COPY.badEmail);
    });
    email.addEventListener('input', function () {
      if (email.getAttribute('aria-invalid')) { markValid(); say(''); }
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (busy || !payable) return;                       // double-submit guard
      var value = email.value.trim();
      // Optional since pay-first: blank means Razorpay's form collects it.
      if (value && !validEmail(value)) { markInvalid(COPY.badEmail); focusQuiet(email); return; }
      markValid();
      say('');
      if (value) remember(value);
      // A code typed but never applied is applied now, and only a code the
      // rail honours lets the payment start.
      var pending = codeInput ? codeInput.value.replace(/^\s+|\s+$/g, '').toUpperCase() : '';
      if (pending && pending !== applied) {
        applyCode(pending, function (ok) {
          if (ok) start(value);
          else focusQuiet(codeInput);
        });
        return;
      }
      if (!pending) applied = '';
      start(value);
    });

    /* ---- paying ---- */

    function start(value) {
      busy = true;
      syncPay();
      state = 'opening';
      lastFailure = '';
      if (status) status.textContent = COPY.opening;
      step('paying');

      var body = cfg.payload(value);
      if (applied) body.code = applied;

      Promise.all([
        loadRazorpay(),
        postJSON(api + '/checkout/create', body, CREATE_TIMEOUT_MS)
      ]).then(function (results) {
        var res = results[1];
        if (res.status !== 200 || !res.data.ok) {
          var err = new Error(res.data.reason || res.data.error || 'could_not_start');
          err.data = res.data;
          throw err;
        }
        var order = res.data;
        checkoutId = order.checkout_id || '';
        emit('create', cfg.detail({
          order: order.rzp_order_id || '',
          amount: order.amount_minor,
          currency: order.currency || '',
          code: (order.discount && order.discount.applicable && order.discount.code) || ''
        }));
        if (cfg.onCreated) cfg.onCreated(order);
        paintPay(order.amount_minor);
        // A code took the cart to zero: the rail settled it in the same
        // call and the grants are already here. No Razorpay, no Purchase
        // event (there was no purchase).
        if (order.free) {
          var grants = (order.grants || []).filter(function (g) { return g && g.token; });
          if (!grants.length) { late('free_no_grant'); return; }
          finish(grants, order.email || value, !!order.emailed, true);
          return;
        }
        handOff(order);
      }).catch(function (e) {
        fail((e && e.message) || 'could_not_start', (e && e.data) || {});
      });
    }

    // Nothing was charged. Back to the email step with the reason inline —
    // unless the customer already forced the sheet shut, in which case reset
    // quietly rather than pop back up at them.
    function fail(reason, data) {
      emit('error', cfg.detail({ reason: reason, paid: false }));
      state = 'email';
      busy = false;
      syncPay();
      step('email');
      if (!sheet.open) { resetIdle(); return; }
      if (!(cfg.onRefused && cfg.onRefused(reason, data || {}))) say(describe(reason), 'error');
      focusQuiet(email);
    }

    function returnToEmail(text, tone) {
      state = 'email';
      busy = false;
      syncPay();
      step('email');
      present();
      say(text, tone);
      focusQuiet(email);
    }

    function handOff(order) {
      state = 'razorpay';
      var rzp;
      try {
        rzp = new window.Razorpay({
          key: order.key_id,
          order_id: order.rzp_order_id,
          amount: order.amount_minor,
          currency: order.currency,
          name: order.brand || 'emporo',
          description: cfg.description(),
          // Only a real address is prefilled; blank lets Razorpay's own
          // form ask, which is the pay-first design.
          prefill: order.email ? { email: order.email } : {},
          theme: { color: RZP_THEME },
          retry: { enabled: true },
          modal: {
            escape: true,
            backdropclose: false,
            // Razorpay's modal was dismissed: nothing charged. Back to the
            // email step; if a payment attempt failed inside, say so.
            ondismiss: function () {
              if (state !== 'razorpay') return;
              emit('dismiss', cfg.detail({ failed: !!lastFailure }));
              if (lastFailure) returnToEmail(COPY.failed, 'error');
              else returnToEmail(COPY.dismissed, 'note');
            }
          },
          handler: function (response) { confirmPayment(response); }
        });
        rzp.on('payment.failed', function (resp) {
          lastFailure = (resp && resp.error && resp.error.code) || 'payment_failed';
          emit('error', cfg.detail({ reason: 'payment_failed', code: lastFailure, paid: false }));
        });
      } catch (e) {
        fail('rzp_open_failed');
        return;
      }

      // Hand the screen to Razorpay: a modal dialog would leave its iframe inert.
      hide();
      closeDrawers();
      try {
        rzp.open();
      } catch (e) {
        fail('rzp_open_failed');
      }
    }

    /* ---- after payment ---- */

    function confirmPayment(response) {
      state = 'confirming';
      if (status) status.textContent = COPY.confirming;
      step('paying');
      present();
      emit('paid', cfg.detail({
        order: response.razorpay_order_id || '',
        payment: response.razorpay_payment_id || ''
      }));

      var payload = {
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature
      };
      lastPaymentId = response.razorpay_payment_id || '';

      // The money has moved; the only thing that can still go wrong is OUR
      // confirmation call. Retry it three times with backoff before showing
      // the late state — a phone on a flaky network should not be told to
      // wait for an email over a two-second blip.
      function attempt(n) {
        postJSON(api + '/checkout/verify', payload, VERIFY_TIMEOUT_MS).then(function (v) {
          var data = v.data || {};
          var grants = (data.grants || []).filter(function (g) { return g && g.token; });
          if (v.status === 200 && data.ok && grants.length) {
            if (data.checkout_id) checkoutId = data.checkout_id;
            // data.email is what the rail holds: the typed one, or the
            // payer's from Razorpay's form. Blank means neither exists.
            finish(grants, data.email || email.value.trim(), !!data.emailed);
          } else if (n < 3 && v.status !== 400) {
            setTimeout(function () { attempt(n + 1); }, 2000 * Math.pow(2, n));
          } else {
            late(data.reason || data.error || 'verify_failed');
          }
        }).catch(function (e) {
          if (n < 3) setTimeout(function () { attempt(n + 1); }, 2000 * Math.pow(2, n));
          else late((e && e.message) || 'verify_failed');
        });
      }
      attempt(0);
    }

    function linkFor(token) { return api + '/d/' + token; }

    // One link per grant, titled by the pack. Markup comes from the snippet's
    // <template>; when there is none, plain anchors.
    function renderLinks(grants) {
      links.innerHTML = '';
      grants.forEach(function (g) {
        var li, a, label;
        if (linkTpl && linkTpl.content) {
          li = linkTpl.content.firstElementChild.cloneNode(true);
          a = li.querySelector('a');
          label = li.querySelector('[data-e-link-title]');
        } else {
          li = document.createElement('li');
          a = document.createElement('a');
          a.className = 'e-btn e-btn--primary e-btn--block e-buy__download e-buy__link';
          label = document.createElement('span');
          a.appendChild(label);
          li.appendChild(a);
        }
        a.setAttribute('href', linkFor(g.token));
        a.setAttribute('data-sku', g.sku || '');
        label.textContent = g.title || 'Download';
        links.appendChild(li);
      });
      links.hidden = false;
    }

    function firstLink() {
      return links ? links.querySelector('a') : download;
    }

    function finish(grants, addr, emailed, free) {
      state = 'done';
      busy = false;
      syncPay();
      if (links) {
        renderLinks(grants);
      } else {
        download.setAttribute('href', linkFor(grants[0].token));
        download.hidden = false;
      }
      var many = grants.length > 1;
      if (doneTitle) {
        doneTitle.textContent = free
          ? (many ? COPY.doneFreePacks : COPY.doneFree)
          : (many ? COPY.donePacks : COPY.doneTitle);
      }
      if (doneBody) { doneBody.hidden = true; doneBody.textContent = ''; }
      // Say "sent to your email" only when the rail says it actually did.
      if (doneNote) {
        doneNote.textContent = emailed
          ? 'Also sent to ' + addr + ' · valid 14 days · 10 downloads' + (many ? ' each' : '')
          : (many
            ? 'Save these links — each valid 14 days · 10 downloads'
            : 'Save this link — valid 14 days · 10 downloads');
      }
      // No address anywhere (pay-first, Razorpay's form let it through
      // blank): offer to take one now. Otherwise the box stays hidden.
      if (doneForm) {
        doneForm.hidden = !!addr || free;
        if (doneMsg) { doneMsg.hidden = true; doneMsg.textContent = ''; }
        if (doneSend) doneSend.disabled = false;
      }
      if (cfg.onFinish) cfg.onFinish(grants, addr, emailed);
      step('done');
      present();
      emit('done', cfg.detail({ delivered: true, grants: grants.length }));
      focusQuiet(firstLink());
    }

    /* ---- the done step's email box (pay-first, no address yet) ---- */

    if (doneForm && doneEmail) {
      doneForm.addEventListener('submit', function (event) {
        event.preventDefault();
        var value = doneEmail.value.trim();
        var tell = function (text, tone) {
          if (!doneMsg) return;
          doneMsg.textContent = text;
          doneMsg.classList.toggle('is-error', tone === 'error');
          doneMsg.hidden = !text;
        };
        if (!value || !validEmail(value)) { tell(COPY.badEmail, 'error'); focusQuiet(doneEmail); return; }
        if (!checkoutId) { tell(COPY.couldNotSend, 'error'); return; }
        if (doneSend) doneSend.disabled = true;
        tell('Sending…', 'note');
        postJSON(api + '/checkout/email', { checkout_id: checkoutId, email: value }, CREATE_TIMEOUT_MS).then(function (res) {
          var data = res.data || {};
          if (res.status !== 200 || !data.ok) {
            if (doneSend) doneSend.disabled = false;
            tell(data.reason === 'bad_email' ? COPY.badEmail : COPY.couldNotSend, 'error');
            return;
          }
          remember(data.email || value);
          doneForm.hidden = true;
          if (doneNote) {
            doneNote.textContent = (data.emailed ? 'Sent to ' : 'Saved for ') + (data.email || value)
              + ' · valid 14 days · 10 downloads';
          }
          emit('email', cfg.detail({ after: true }));
        }).catch(function () {
          if (doneSend) doneSend.disabled = false;
          tell(COPY.couldNotSend, 'error');
        });
      });
    }

    // Payment succeeded; only OUR confirmation call failed. Never a payment
    // error: the webhook settles it server-side and the email goes out anyway.
    function late(reason) {
      state = 'done';
      busy = false;
      syncPay();
      if (links) { links.innerHTML = ''; links.hidden = true; }
      if (download) { download.hidden = true; download.setAttribute('href', '#'); }
      if (doneTitle) doneTitle.textContent = COPY.lateTitle;
      if (doneBody) { doneBody.textContent = COPY.lateBody; doneBody.hidden = false; }
      if (doneNote) doneNote.textContent = lastPaymentId ? 'Payment id ' + lastPaymentId : '';
      if (cfg.onLate) cfg.onLate();
      step('done');
      present();
      emit('error', cfg.detail({ reason: 'verify_failed', detail: reason, paid: true }));
      emit('done', cfg.detail({ delivered: false }));
      focusQuiet(closeBtn || sheet);
    }

    /* ---- the surface the two modes use ---- */

    ctrl.open = open;
    ctrl.dismiss = dismiss;
    ctrl.present = present;
    ctrl.say = say;
    ctrl.inFlight = inFlight;
    ctrl.isBusy = function () { return busy; };
    ctrl.isOpen = function () { return !!sheet.open; };
    ctrl.setPayable = function (can) { payable = !!can; syncPay(); };
    ctrl.focusEmail = function () { focusQuiet(email); };
    ctrl.applied = function () { return applied; };
    // A code Shopify's cart already carries (typed on the cart page or in a
    // link) is shown and applied without the buyer typing it twice.
    ctrl.setCode = function (code) {
      if (!codeInput) return;
      if (codeWrap) {
        codeWrap.hidden = false;
        if (codeToggle) codeToggle.setAttribute('aria-expanded', 'true');
      }
      applyCode(code);
    };
    ctrl.paintPay = paintPay;
    return ctrl;
  }

  /* ---- product mode: one buy box, one SKU ------------------------------- */

  function initBuy(root) {
    if (root.__eBuy) return;
    var api = (root.getAttribute('data-api') || '').replace(/\/+$/, '');
    var sku = root.getAttribute('data-sku') || '';
    var title = root.getAttribute('data-title') || '';
    var openBtn = root.querySelector('[data-e-buy-open]');
    if (!api || !sku || !openBtn) return;

    var moneyFormat = root.getAttribute('data-money-format') || '';
    var baseMinor = Number(root.getAttribute('data-amount-minor')) || 0;
    var priceNow = root.querySelector('[data-e-price-now]');
    var priceWas = root.querySelector('[data-e-price-was]');
    var priceSave = root.querySelector('[data-e-price-save]');
    // What the sheet showed before any code, so un-applying restores it
    // (a compare-at price included).
    var shown = {
      now: priceNow ? priceNow.textContent : '',
      was: priceWas ? priceWas.textContent : '', wasHidden: priceWas ? priceWas.hidden : true,
      save: priceSave ? priceSave.textContent : '', saveHidden: priceSave ? priceSave.hidden : true
    };
    function money(minor) { return formatMoney(minor, moneyFormat, ''); }

    var s = Sheet(root, {
      api: api,
      payload: function (addr) { return { sku: sku, email: addr }; },
      description: function () { return title; },
      detail: function (extra) { return merge({ sku: sku }, extra); },
      money: money,
      base: function () { return baseMinor; },
      onQuote: function (q) {
        if (!priceNow) return;
        if (!q) {
          priceNow.textContent = shown.now;
          if (priceWas) { priceWas.textContent = shown.was; priceWas.hidden = shown.wasHidden; }
          if (priceSave) { priceSave.textContent = shown.save; priceSave.hidden = shown.saveHidden; }
          return;
        }
        priceNow.textContent = money(q.amount_minor);
        if (priceWas) { priceWas.textContent = money(baseMinor); priceWas.hidden = false; }
        if (priceSave) {
          var off = q.discount ? q.discount.amount_minor : 0;
          priceSave.textContent = off ? q.discount.code + ' −' + money(off) : '';
          priceSave.hidden = !off;
        }
      }
    });
    if (!s) return;
    root.__eBuy = true;

    openBtn.addEventListener('click', function () {
      s.returnTo = openBtn;
      s.open();
    });
  }

  /* ---- cart mode: the one global sheet, the cart's SKUs ----------------- */

  var cartSheet = null;

  function initCart(root) {
    if (root.__eBuy) return;
    var api = (root.getAttribute('data-api') || '').replace(/\/+$/, '');
    var routes = (window.Theme && window.Theme.routes) || {};
    var cartUrl = (root.getAttribute('data-cart-url') || routes.cart_url || '/cart').replace(/\/+$/, '') || '/cart';
    var moneyFormat = root.getAttribute('data-money-format') || '';
    var list = root.querySelector('[data-e-items]');
    var totalEl = root.querySelector('[data-e-total]');
    var payAmount = root.querySelector('[data-e-pay-amount]');
    var leftOut = root.querySelector('[data-e-left-out]');
    var removeBtn = root.querySelector('[data-e-remove]');
    var clearBtn = root.querySelector('[data-e-clear]');
    var clearMsg = root.querySelector('[data-e-clear-msg]');
    var itemTpl = root.querySelector('[data-e-item-template]');
    if (!api || !list || !totalEl) return;

    var lines = [];        // [{sku, key, title, price}] — one per distinct SKU
    var skus = [];
    var total = 0;
    var currency = '';
    var badLine = null;
    var seq = 0;

    var s = Sheet(root, {
      api: api,
      instant: false,      // the cart sheet shows its lines and total before paying
      payload: function (addr) { return { skus: skus.slice(), email: addr }; },
      description: function () {
        return lines.length === 1 ? lines[0].title : lines.length + ' packs';
      },
      detail: function (extra) {
        return merge({ mode: 'cart', skus: skus.slice(), count: skus.length, amount: total, currency: currency }, extra);
      },
      money: function (minor) { return money(minor); },
      base: function () { return lines.reduce(function (a, l) { return a + l.base; }, 0); },
      // The rail is the authority on every amount. Its items[] carry the
      // price it will charge per SKU and amount_minor the sum (a quote with
      // a code, or the order itself); the sheet paints those numbers.
      // Null puts the base prices back.
      onQuote: function (q) {
        if (!q) {
          lines.forEach(function (l) { l.price = l.base; });
          total = lines.reduce(function (a, l) { return a + l.price; }, 0);
          if (totalWas) totalWas.hidden = true;
          paintLines();
          return;
        }
        applyRail(q);
      },
      onCreated: function (order) { applyRail(order); },
      onRefused: refused,
      onFinish: function () {
        if (clearBtn) {
          clearBtn.hidden = false;
          clearBtn.disabled = false;
          clearBtn.textContent = 'Clear cart';
        }
        if (clearMsg) { clearMsg.hidden = true; clearMsg.textContent = ''; }
      },
      onLate: function () {
        if (clearBtn) clearBtn.hidden = true;
        if (clearMsg) clearMsg.hidden = true;
      }
    });
    if (!s) return;
    root.__eBuy = true;

    function money(minor) { return formatMoney(minor, moneyFormat, currency); }

    var totalWas = root.querySelector('[data-e-total-was]');

    function applyRail(q) {
      var changed = false;
      if (q.currency && q.currency !== currency) { currency = q.currency; changed = true; }
      (q.items || []).forEach(function (it) {
        lines.forEach(function (l) {
          if (l.sku === it.sku && typeof it.amount_minor === 'number' && it.amount_minor !== l.price) {
            l.price = it.amount_minor;
            changed = true;
          }
        });
      });
      if (typeof q.amount_minor === 'number' && q.amount_minor !== total) {
        total = q.amount_minor;
        changed = true;
      }
      var off = q.discount && q.discount.applicable ? Number(q.discount.amount_minor) || 0 : 0;
      if (totalWas) {
        totalWas.hidden = !off;
        totalWas.textContent = off ? money(total + off) : '';
      }
      if (changed) paintLines();
    }

    // Every cart-items-component on the page, so the drawer and the cart
    // page both re-render from the sections Shopify returns.
    function sectionIds() {
      var ids = ['cart-drawer-section'];
      document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
        var id = el.getAttribute('data-section-id');
        if (id && ids.indexOf(id) < 0) ids.push(id);
      });
      return ids.join(',');
    }

    // Tell Horizon (drawer, icon, count) the cart changed. Same event and
    // promise shape its own components dispatch; sections included so the
    // drawer morphs without a refetch.
    function announce(cart, action) {
      var ev;
      try { ev = new CustomEvent(CART_EVENT, { bubbles: true }); } catch (e) { return; }
      var count = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
      ev.action = action;
      ev.context = 'cart';
      ev.lines = [];
      ev.promise = Promise.resolve({
        cart: { totalQuantity: count },
        detail: {
          sections: (cart && cart.sections) || {},
          items: (cart && cart.items) || [],
          itemCount: count,
          source: 'emporo-cart-sheet',
          didError: false
        }
      });
      document.dispatchEvent(ev);
    }

    function paint() {
      totalEl.textContent = money(total);
      if (payAmount) payAmount.textContent = money(total);
    }

    // Bring Shopify's cart in line with "one file, once": quantities above 1
    // become 1 and duplicate lines go. Best effort, sequential (the cart API
    // rejects concurrent changes), and the standard event refreshes the
    // drawer, badge and count when it is done.
    function normaliseCart(fixes) {
      var chain = Promise.resolve();
      fixes.forEach(function (fx) {
        chain = chain.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(fx)
          }).then(function (r) { return r.json(); }).catch(function () { return null; });
        });
      });
      chain.then(function (cart) {
        if (!cart) return;
        try {
          var ev = new CustomEvent('shopify:cart:lines-update', { bubbles: true, detail: { source: 'emporo-cart-sheet', items: cart.items, itemCount: cart.item_count } });
          ev.promise = Promise.resolve({ cart: { totalQuantity: cart.item_count }, detail: ev.detail });
          document.dispatchEvent(ev);
        } catch (e) { /* cosmetic */ }
      });
    }

    // Read /cart.js: one line per distinct SKU, blanks left out, the total
    // as Shopify sees it (the rail's own numbers replace these on create).
    function take(cart) {
      currency = (cart && cart.currency) || currency;
      var seen = {};
      var left = 0;
      lines = [];
      var fixes = [];
      ((cart && cart.items) || []).forEach(function (it) {
        var sku = String(it.sku || '').replace(/^\s+|\s+$/g, '');
        if (!sku) { left += 1; return; }
        // A file bought twice is the same file: one line per SKU, quantity 1.
        // Shopify's cart happily holds "6 x Fable Config" — normalise it so
        // the drawer's total matches what the rail will actually charge.
        if (seen[sku]) { if (it.key) fixes.push({ id: it.key, quantity: 0 }); return; }
        if (Number(it.quantity) > 1 && it.key) fixes.push({ id: it.key, quantity: 1 });
        seen[sku] = true;
        // Shopify's cart may already carry a code (final_price is net of
        // it); the sheet shows the base and lets the rail's quote take the
        // code off, so what is on screen is always what will be charged.
        var base = Number(it.original_price) || Number(it.final_price) || 0;
        lines.push({
          sku: sku,
          key: it.key || '',
          title: it.product_title || it.title || sku,
          price: base,
          base: base
        });
      });
      skus = lines.map(function (l) { return l.sku; });
      total = lines.reduce(function (a, l) { return a + l.price; }, 0);
      if (totalWas) totalWas.hidden = true;
      paintLines();
      s.paintPay(total);
      var carried = null;
      ((cart && cart.discount_codes) || []).forEach(function (d) {
        if (!carried && d && d.applicable && d.code) carried = String(d.code).toUpperCase();
      });
      if (carried && s.applied() !== carried) s.setCode(carried);
      if (fixes.length) normaliseCart(fixes);
      if (leftOut) {
        leftOut.hidden = !left;
        leftOut.textContent = left
          ? (left === 1 ? '1 item without a pack code was left out.' : left + ' items without a pack code were left out.')
          : '';
      }
      badLine = null;
      if (removeBtn) removeBtn.hidden = true;
      s.setPayable(lines.length > 0);
      if (!lines.length) s.say(COPY.nothingToBuy, 'error');
    }

    function paintLines() {
      list.innerHTML = '';
      lines.forEach(function (l) {
        var li, t, p;
        if (itemTpl && itemTpl.content) {
          li = itemTpl.content.firstElementChild.cloneNode(true);
          t = li.querySelector('[data-e-item-title]');
          p = li.querySelector('[data-e-item-price]');
        } else {
          li = document.createElement('li');
          li.className = 'e-buy__item';
          t = document.createElement('span');
          p = document.createElement('span');
          li.appendChild(t);
          li.appendChild(p);
        }
        li.setAttribute('data-sku', l.sku);
        t.textContent = l.title;
        p.textContent = money(l.price);
        list.appendChild(li);
      });
      paint();
    }

    // The rail refused one SKU (unknown or unpriced — a gated pack, or a
    // product the rail has never heard of). Name it and offer to drop it.
    function refused(reason, data) {
      if (reason === 'no_items') { s.say(COPY.nothingToBuy, 'error'); s.setPayable(false); return true; }
      if (reason !== 'unknown_sku' && reason !== 'no_price_for_sku') return false;
      var sku = String(data.sku || (data.skus && data.skus[0]) || '');
      var line = null;
      lines.forEach(function (l) { if (l.sku === sku) line = l; });
      if (!line && lines.length === 1) line = lines[0];
      if (!line) { s.say(COPY.someNotForSale, 'error'); return true; }
      badLine = line;
      s.say(line.title + ' is not available to buy right now.', 'error');
      if (removeBtn) { removeBtn.hidden = false; removeBtn.disabled = false; }
      return true;
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        if (!badLine || !badLine.key) return;
        removeBtn.disabled = true;
        postJSON(cartUrl + '/change.js', {
          id: badLine.key,
          quantity: 0,
          sections: sectionIds(),
          sections_url: window.location.pathname
        }, CART_TIMEOUT_MS).then(function (res) {
          if (res.status !== 200 || !res.data || !res.data.items) throw new Error('change_failed');
          announce(res.data, 'remove');
          take(res.data);
          if (lines.length) s.say(COPY.removed, 'note');
          s.focusEmail();
        }).catch(function () {
          removeBtn.disabled = false;
          s.say(COPY.removeFailed, 'error');
        });
      });
    }

    // The packs are paid for; leaving them in the cart invites a double buy.
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        clearBtn.disabled = true;
        clearBtn.textContent = COPY.clearing;
        postJSON(cartUrl + '/clear.js', {
          sections: sectionIds(),
          sections_url: window.location.pathname
        }, CART_TIMEOUT_MS).then(function (res) {
          if (res.status !== 200) throw new Error('clear_failed');
          announce(res.data || { item_count: 0, items: [] }, 'remove');
          clearBtn.textContent = COPY.cleared;
          if (clearMsg) { clearMsg.hidden = true; clearMsg.textContent = ''; }
        }).catch(function () {
          clearBtn.disabled = false;
          clearBtn.textContent = 'Clear cart';
          if (clearMsg) { clearMsg.textContent = COPY.clearFailed; clearMsg.hidden = false; }
        });
      });
    }

    // A Shopify checkout entry was used: read the cart, then open. Empty
    // cart: nothing (its button is disabled anyway). While a payment is in
    // flight the sheet is simply brought back.
    function openFromCart(trigger) {
      if (s.isBusy()) { s.present(); return; }
      var my = ++seq;
      if (trigger && trigger.setAttribute) trigger.setAttribute('aria-busy', 'true');
      getJSON(cartUrl + '.js', CART_TIMEOUT_MS).then(function (res) {
        if (my !== seq) return;
        if (trigger && trigger.removeAttribute) trigger.removeAttribute('aria-busy');
        var cart = (res.status === 200 && res.data) ? res.data : null;
        if (!cart || !cart.items) throw new Error('cart_unreadable');
        if (!cart.items.length) return;
        s.returnTo = trigger || null;
        s.open(function () { take(cart); });
      }).catch(function () {
        if (my !== seq) return;
        if (trigger && trigger.removeAttribute) trigger.removeAttribute('aria-busy');
        s.returnTo = trigger || null;
        if (!s.open()) return;
        list.innerHTML = '';
        lines = []; skus = []; total = 0;
        paint();
        s.setPayable(false);
        s.say(COPY.cartUnreadable, 'error');
      });
    }

    cartSheet = { openFromCart: openFromCart };
  }

  /* ---- the intercept: every Shopify checkout entry, delegated ----------- */

  var delegated = false;
  function delegate() {
    if (delegated) return;
    delegated = true;

    document.addEventListener('click', function (event) {
      if (!cartSheet) return;
      if (typeof event.button === 'number' && event.button !== 0) return;
      var t = closest(event.target, CHECKOUT_SEL);
      if (!t) return;
      if (closest(t, '[data-e-sheet]')) return;          // our own sheets' buttons
      if (t.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      cartSheet.openFromCart(t);
    }, true);

    // Enter in a field of the cart form, or a checkout form submitted by
    // script: same door.
    document.addEventListener('submit', function (event) {
      if (!cartSheet) return;
      var form = event.target;
      if (!form || form.nodeName !== 'FORM') return;
      if (closest(form, '[data-e-sheet]')) return;
      var submitter = event.submitter || null;
      var action = String(form.getAttribute('action') || '');
      var toCheckout = action.indexOf('/checkout') >= 0 ||
        (submitter && String(submitter.getAttribute('name') || '') === 'checkout');
      if (!toCheckout) return;
      event.preventDefault();
      event.stopPropagation();
      cartSheet.openFromCart(submitter || form);
    }, true);
  }

  /* ---- boot ------------------------------------------------------------- */

  function boot() {
    document.querySelectorAll('[data-e-buy]').forEach(function (el) {
      try { initBuy(el); } catch (e) { console.warn('[emporo] checkout failed', e); }
    });
    document.querySelectorAll('[data-e-cart-sheet]').forEach(function (el) {
      try { initCart(el); } catch (e) { console.warn('[emporo] cart checkout failed', e); }
    });
    delegate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', function () {
    // The editor swapped a section out from under an open sheet: drop the lock
    // unless another sheet is still open.
    if (!document.querySelector('[data-e-sheet][open]')) {
      document.documentElement.classList.remove(LOCK_CLASS);
    }
  });
})();
